import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  CodeInfo,
  GetConfirmedInspection,
  PutConfirmedInspection,
  GetPFRCodeTableRow,
  Status,
  CdCodeInfo,
} from '../../services/apiTypes';
import {
  ConfirmedInspectionParam,
  fetchConfirmedInspection as fetchConfirmedInspectionAPI,
  updateConfirmedInspection as updateConfirmedInspectionAPI,
} from './api';
import { isGetConfirmedInspection } from '../../_utils/typeGuardUtils';

interface ConfirmedInspectionState {
  status: Status;
  confirmedInspection: GetConfirmedInspection;
}

const initialConfirmedInspection: GetConfirmedInspection = {
  rmaNo: '',
  questions: [],
  repairType: null,
  repairTypeList: [],
  serviceType: '',
  serviceTypeList: [],
  evaluationResults: '',
  notesToCustomer: '',
  documents: [
    { id: 0, comment: '', documentPath: '', imagePath: '' },
    { id: 1, comment: '', documentPath: '', imagePath: '' },
  ],
  cdCodeList: [],
  pfrCodeList: {
    before: [],
    after: [],
  },
  isaReplacement: {
    main: { code: '', detail: '' },
    sub: { code: '', detail: '' },
  },
};

const initialState: ConfirmedInspectionState = {
  status: 'idle',
  confirmedInspection: initialConfirmedInspection,
};

export const fetchConfirmedInspection = createAsyncThunk(
  'confirmedInspection/fetchConfirmedInspection',
  async (params: ConfirmedInspectionParam, { rejectWithValue }) => {
    try {
      return await fetchConfirmedInspectionAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateConfirmedInspection = createAsyncThunk(
  'confirmedInspection/updateConfirmedInspection',
  async (
    newConfirmedInspection: PutConfirmedInspection,
    { rejectWithValue },
  ) => {
    try {
      return await updateConfirmedInspectionAPI(newConfirmedInspection);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const confirmedInspectionSlice = createSlice({
  name: 'confirmedInspection',
  initialState,
  reducers: {
    setAnswer: (
      state,
      action: PayloadAction<{ id: number; answer: string }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.questions[action.payload.id].answer =
          action.payload.answer;
      }
    },
    setQuestionMemo: (
      state,
      action: PayloadAction<{ id: number; memo: string }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.questions[action.payload.id].memo =
          action.payload.memo;
      }
    },
    setRepairType: (state, action: PayloadAction<number | null>) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.repairType = action.payload;
      }
    },
    setServiceType: (state, action: PayloadAction<string>) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.serviceType = action.payload;
      }
    },
    setEvaluationResults: (state, action: PayloadAction<string>) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.evaluationResults = action.payload;
      }
    },
    setNotesToCustomer: (state, action: PayloadAction<string>) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.notesToCustomer = action.payload;
      }
    },
    setFilePath: (
      state,
      action: PayloadAction<{ id: number; path: string }>,
    ) => {
      if (state.confirmedInspection) {
        let isSet = false;
        state.confirmedInspection.documents.forEach((item, i) => {
          if (item.id === action.payload.id) {
            state.confirmedInspection!.documents[i].imagePath =
              action.payload.path;
            isSet = true;
          }
        });
        if (!isSet) {
          state.confirmedInspection.documents.push({
            id: action.payload.id,
            comment: '',
            documentPath: '',
            imagePath: action.payload.path,
          });
        }
      }
    },
    setDocumentPath: (
      state,
      action: PayloadAction<{ id: number; path: string }>,
    ) => {
      if (state.confirmedInspection) {
        let isSet = false;
        state.confirmedInspection.documents.forEach((item, i) => {
          if (item.id === action.payload.id) {
            state.confirmedInspection!.documents[i].documentPath =
              action.payload.path;
            isSet = true;
          }
        });
        if (!isSet) {
          state.confirmedInspection.documents.push({
            id: action.payload.id,
            comment: '',
            documentPath: action.payload.path,
            imagePath: '',
          });
        }
      }
    },
    setDocumentComment: (
      state,
      action: PayloadAction<{ id: number; comment: string }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.documents.forEach((item, i) => {
          if (item.id === action.payload.id) {
            state.confirmedInspection!.documents[i].comment =
              action.payload.comment;
          }
        });
      }
    },
    setAfterIndex: (
      state,
      action: PayloadAction<{ newAfter: GetPFRCodeTableRow[] }>,
    ) => {
      if (state.confirmedInspection) {
        action.payload.newAfter.forEach((newItem, newIndex) => {
          state.confirmedInspection!.pfrCodeList.after.forEach((item) => {
            if (newItem.itemId === item.itemId) {
              item.index = newIndex;
            }
          });
        });
        state.confirmedInspection.pfrCodeList.after =
          state.confirmedInspection.pfrCodeList.after.sort(
            (a, b) => a.index - b.index,
          );
      }
    },
    setReportedByCustomer: (
      state,
      action: PayloadAction<{ index: number; value: boolean }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.pfrCodeList.after[
          action.payload.index
        ].reportedByCustomer = action.payload.value;
      }
    },
    setCdCode: (
      state,
      action: PayloadAction<{ index: number; value: CdCodeInfo }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.pfrCodeList.after[action.payload.index].cd =
          action.payload.value;
      }
    },
    setSelectedR: (
      state,
      action: PayloadAction<{ index: number; value: CodeInfo }>,
    ) => {
      if (state.confirmedInspection) {
        state.confirmedInspection.pfrCodeList.after[action.payload.index].r =
          action.payload.value;
      }
    },
    setRestoreSelectedR: (
      state,
      action: PayloadAction<{ values: GetPFRCodeTableRow[] }>,
    ) => {
      if (state.confirmedInspection) {
        const newAfter = [...state.confirmedInspection?.pfrCodeList.after];
        action.payload.values.forEach((item) => {
          newAfter.forEach((findItem, index) => {
            if (
              findItem.f.code === item.f.code &&
              findItem.p.code === item.p.code
            ) {
              newAfter[index].r = item.r;
            }
          });
        });
        state.confirmedInspection.pfrCodeList.after = newAfter;
      }
    },
    resetConfirmedInspection: (state) => {
      state.confirmedInspection = initialConfirmedInspection;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchConfirmedInspection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchConfirmedInspection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetConfirmedInspection(action.payload)) {
          state.confirmedInspection = action.payload;
          action.payload.pfrCodeList.after.forEach((row, i) => {
            if (row.rList.length === 1) {
              state.confirmedInspection.pfrCodeList.after[i].r.code =
                row.rList[0].code;
              state.confirmedInspection.pfrCodeList.after[i].r.detail =
                row.rList[0].detail;
            }
          });
        }
      })
      .addCase(fetchConfirmedInspection.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updateConfirmedInspection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateConfirmedInspection.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateConfirmedInspection.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const {
  setAnswer,
  setQuestionMemo,
  setRepairType,
  setServiceType,
  setEvaluationResults,
  setNotesToCustomer,
  setFilePath,
  setDocumentPath,
  setDocumentComment,
  setAfterIndex,
  setReportedByCustomer,
  setCdCode,
  setSelectedR,
  setRestoreSelectedR,
  resetConfirmedInspection,
} = confirmedInspectionSlice.actions;

export default confirmedInspectionSlice.reducer;
